/**
 * @author simone.bosco <simone.bosco@sgyagency.com.au>
 * @author SGY
 * @since 03/2023
 * @copyright © SGY
 */
/**
 * This entity represents a collective of methods to calculate a Taxation
 * snapshot for a Consumer, using Regulations, at a specific point in time.
 * Taxation is an abstraction of government taxes and benefits on one year.
 * Do not modify this data freely as it needs to match the model / domain.
 */
const sgyTaxation = function(regulations, consumer, unallocated_contribution) {

  this.income = typeof consumer.startSal !== 'undefined' ? consumer.startSal : 0;
  this.unallocated_contribution = typeof unallocated_contribution !== 'undefined' ? unallocated_contribution : 0;
  this.age = typeof consumer.age !== 'undefined' ? consumer.age : 30;
  this.sgc = typeof consumer.assumSGCContPerc !== 'undefined' ? consumer.assumSGCContPerc : 0;
  this.before = typeof consumer.beforeTaxDollar !== 'undefined' ? consumer.beforeTaxDollar : 0;
  this.after = typeof consumer.afterTaxDollar !== 'undefined' ? consumer.afterTaxDollar : 0;

  this.sgcDollarFind = function() {
    return Math.min(regulations.maxSuperContBase, this.income) * this.sgc;
  }

  this.incomeTaxOnlyFind = function(income) {
    var k;
    for (k = 0; k < regulations.incomeTaxArray.length; k++) {
      if (income <= regulations.incomeTaxArray[k][0]) {
        return ((income - regulations.incomeTaxArray[k][3]) * regulations.incomeTaxArray[k][1]) + regulations.incomeTaxArray[k][2];
      }
    }
    k = regulations.incomeTaxArray.length - 1;
    return ((income - regulations.incomeTaxArray[k][3]) * regulations.incomeTaxArray[k][1]) + regulations.incomeTaxArray[k][2];
  }
  this.incomeTaxDifference = function() {
    return this.incomeTaxOnlyFind(this.income) - this.incomeTaxOnlyFind(this.income - this.before);
  }

  this.medicareLevyFind = function(income) {
    if (income <= regulations.medicareLevyLowerThreshold) {
      return 0;
    } else {
      if (income > regulations.medicareLevyLowerThreshold && income <= regulations.medicareLevyUpperThreshold) {
        return regulations.medicareLevyTaper * (income - regulations.medicareLevyLowerThreshold);
      } else {
        return income * regulations.medicareLevy;
      }
    }
  }
  this.medicareLevyDifference = function() {
    return this.medicareLevyFind(this.income) - this.medicareLevyFind(this.income - this.before);
  }

  this.lowIncomeTaxOffsetFind = function(income) {
    if (income <= regulations.litoLowerLimit) {
      return regulations.litoMaxReturnOne;
    } else if ((income > regulations.litoLowerLimit) && (income <= regulations.litoMiddleLimit)) {
      return regulations.litoMaxReturnOne - ((income - regulations.litoLowerLimit) * regulations.litoTaperRateOne);
    } else if ((income > regulations.litoMiddleLimit) && (income <= regulations.litoUpperLimit)) {
      return regulations.litoMaxReturnTwo - ((income - regulations.litoMiddleLimit) * regulations.litoTaperRateTwo);
    }
    return 0;
  }
  this.litoDifference = function() {
    return this.lowIncomeTaxOffsetFind(this.income) - this.lowIncomeTaxOffsetFind(this.income - this.before);
  }

  this.takeHomePay = function(income) {
    return income - this.incomeTaxOnlyFind(income) - this.medicareLevyFind(income) + this.lowIncomeTaxOffsetFind(income);
  }
  this.takeHomePayDifference = function() {
    return this.takeHomePay(this.income) - this.takeHomePay(this.income - this.before);
  }

  this.lowIncomeSuperTaxOffsetFind = function(income, cont_before) {
    cont_before = typeof cont_before !== 'undefined' ? cont_before : 0;
    if (income <= regulations.listoMaxIncome) {
      return Math.max(0, Math.min((income * this.sgc + cont_before) * regulations.listoTaxRate, regulations.listoMaxRebate));
    }
    return 0;
  }
  this.listoDifference = function() {
    return this.lowIncomeSuperTaxOffsetFind(this.income) - this.lowIncomeSuperTaxOffsetFind(this.income - this.before, this.before);
  }

  this.contributionTaxFind = function(income, cont_before) {
    cont_before = typeof cont_before !== 'undefined' ? cont_before : 0;
    let taxes_on_sgc = this.sgcDollarFind() * regulations.contributionTaxRate;
    let taxes_on_before = cont_before * regulations.contributionTaxRate;
    let taxes_on_exceeding = 0;
    let income_and_sgc = income + this.sgcDollarFind();
    //division 293 rule
    if (income_and_sgc > regulations.contributionTaxThreshold) {
      let exceeding_income = income_and_sgc - regulations.contributionTaxThreshold;
      taxes_on_exceeding = exceeding_income * regulations.contributionTaxRateMax;
    }
    return taxes_on_sgc + taxes_on_before + taxes_on_exceeding;
  }
  this.contributionTaxDifference = function() {
    return this.contributionTaxFind(this.income) - this.contributionTaxFind(this.income - this.before, this.before);
  }

  this.coContributionFind = function(income, cont_after) {
    cont_after = typeof cont_after !== 'undefined' ? cont_after : 0;
    let ret = 0;
    if (this.age > 70 || income > regulations.coContThresholdMax) {
      ret = 0;
    } else if (income <= regulations.coContThresholdMin) {
      ret = Math.min(regulations.coContMax, regulations.coContRatePerc * cont_after);
    } else {
      ret = Math.min(regulations.coContRatePerc * cont_after, regulations.coContMax - regulations.coContReductionRate * (income - regulations.coContThresholdMin));
    }

    return Math.round(ret, 0.05); // Rounds to nearest 5 cents
  }
  this.coContributionDifference = function() {
    return this.coContributionFind(this.income) - this.coContributionFind(this.income - this.before, this.after);
  }

  this.allocateContribution = function() {
    // if we have these set in the customer already it means we are after a manual scenario not an optimal one and we don't split
    if (this.before != 0 || this.after != 0 || this.unallocated_contribution == 0) return false;
    /*
     * 1) Maximise co-contribution
     * 2) Maximise tax Differences through salsac
     *		without exceeding the contribution cap (25000 )
     *		and without dropping taxable income below 20540
     * 3) Divert any remaining contributions after tax
     */
    // work out the tax free threshold taking into account LITO
    let litoTaxFree = Math.round((regulations.litoMaxReturnOne / regulations.incomeTaxArray[1][1]) + regulations.incomeTaxArray[1][3]);
    // find maxmimum co-cont available
    let cocont = this.coContributionFind(this.income, this.unallocated_contribution);
    // divide by contribution rate to find best after-tax amount
    let after = cocont / regulations.coContRatePerc;
    // remaining contribution is then tested against before tax rules
    let remaining = this.unallocated_contribution - after;
    // find max contribution without exceeding cont cap
    let superD = regulations.concessionalLimit - this.sgcDollarFind();
    // find max contribution to prevent dropping below 20540
    let maxD = Math.max(0, this.income - litoTaxFree);
    // so max before tax contribution is
    let maxBefore = Math.max(0, Math.min(maxD, superD));
    // thus, before tax is
    let before = Math.min(maxBefore, remaining);
    // we now have a 2nd remaining contribution
    let remaining2 = remaining - before;
    // which is added to after tax
    after += remaining2;

    this.after = after;
    this.before = before;

    return true;
  }

  if (this.before == 0 && this.after == 0 && this.unallocated_contribution != 0) this.allocateContribution();

  return {
    contributions: {
      // contribution split
      mode: (this.unallocated_contribution != 0) ? 'smart' : 'manual',
      allocated: this.unallocated_contribution,
      before: this.before,
      after: this.after,
      manual_before: consumer.beforeTaxDollar,
      manual_after: consumer.afterTaxDollar
    },
    pre_contributions: {
      // before setting contributions
      income: this.income,
      sgc: this.sgcDollarFind(),
      income_tax: this.incomeTaxOnlyFind(this.income),
      medicare_levy: this.medicareLevyFind(this.income),
      lito: this.lowIncomeTaxOffsetFind(this.income),
      take_home_pay: this.takeHomePay(this.income),
      listo: this.lowIncomeSuperTaxOffsetFind(this.income),
      contribution_tax: this.contributionTaxFind(this.income),
      co_contribution: this.coContributionFind(this.income)
    },
    post_contributions: {
      // after setting contribution either smart or manual
      income: this.income - this.before,
      sgc: this.sgcDollarFind(),
      income_tax: this.incomeTaxOnlyFind(this.income - this.before),
      medicare_levy: this.medicareLevyFind(this.income - this.before),
      lito: this.lowIncomeTaxOffsetFind(this.income - this.before),
      take_home_pay: this.takeHomePay(this.income - this.before),
      listo: this.lowIncomeSuperTaxOffsetFind(this.income - this.before, this.before),
      contribution_tax: this.contributionTaxFind(this.income - this.before, this.before),
      co_contribution: this.coContributionFind(this.income - this.before, this.after)
    },
    differences: {
      //always pre - post, the positive or negative effect depends on the context
      income_tax: this.incomeTaxDifference(), //having less is good, more bad. no negation
      medicare_levy: this.medicareLevyDifference(), //having less is good, more bad. no negation
      lito: -this.litoDifference(), //having less is bad, more is good. negating
      take_home_pay: -this.takeHomePayDifference(), //having less is bad, more is good. negating
      listo: -this.listoDifference(), //having less is bad, more is good. negating
      contribution_tax: this.contributionTaxDifference(), //having less is good, more bad. no negation
      co_contribution: -this.coContributionDifference() //having less is bad, more is good. negating
    }
  }

}

module.exports = {
  sgyTaxation,
}
